/**
 * Router-to-router latency matrix for the IS-IS domain (CTX-841).
 *
 * There is no end-to-end delay probe between routers that are not
 * adjacent: PM only measures each link. A cell is therefore the sum of the
 * one-way link delays along the path SPF would choose, under one of two
 * path metrics:
 *
 *   - `igp`       — the wide IS-IS metric, per direction (`forwardMetric` /
 *                   `reverseMetric`). This is algo 0 (DEFAULT), the path most
 *                   customer services actually ride.
 *   - `min-delay` — the advertised min unidirectional delay, the metric
 *                   RFC 9350 uses for a delay flex-algo (algo 128, LATENCY).
 *                   A link that advertises no delay is left out of that SPF,
 *                   as the routers do.
 *
 * Why this does not reuse `computeIsisSpt`: that SPT is undirected (one
 * cost for both directions) and keeps a single parent per node. Here both
 * matter — the domain has asymmetric costs (e.g. 50 one way, 10 the other)
 * and 41 of 306 router pairs have equal-cost paths whose delays differ by up
 * to 4x (one branch detours through Dallas). Hiding those behind a single
 * number would report a latency that only part of the traffic sees.
 *
 * The carrier `*_STRICT` flex-algos are not offered: their paths depend on
 * affinity constraints from the flex-algo definitions, which the topology
 * payload does not carry yet (`flexAlgoDefinitions` is absent).
 */
import { BaselineData, TopologyData, TopologyLink, LatencyDetail } from '../TopologyData';

export type LatencyPathMetric = 'igp' | 'min-delay';

/** Upper bound on equal-cost paths kept per router pair. */
const MAX_ROUTES_PER_PAIR = 16;

export interface LatencyHop {
    from: string;
    to: string;
    linkId: string;
    /** One-way average delay in the traversal direction (µs). */
    delayUs: number | null;
    /** True when this direction does not report and the opposite one was used. */
    borrowed: boolean;
}

export interface LatencyRoute {
    hops: LatencyHop[];
    /** Sum of hop delays; null when any hop reports in neither direction. */
    delayUs: number | null;
    /** Same hops summed over the baseline; null when any hop lacks one. */
    baselineUs: number | null;
}

export interface LatencyCell {
    /** Path cost under the selected metric (IGP units or µs). */
    cost: number;
    /** Equal-cost paths, fastest first. */
    routes: LatencyRoute[];
    minUs: number | null;
    maxUs: number | null;
    /** True when more than `MAX_ROUTES_PER_PAIR` equal-cost paths exist. */
    truncated: boolean;
    /** True when any hop on any route borrowed the opposite direction. */
    hasBorrowedHop: boolean;
}

export interface LatencyMatrixResult {
    routers: string[];
    /** `cells[from][to]`; absent when `to` is unreachable from `from`. */
    cells: Record<string, Record<string, LatencyCell>>;
}

interface DirectedEdge {
    from: string;
    to: string;
    linkId: string;
    cost: number;
    delayUs: number | null;
    borrowed: boolean;
    baselineUs: number | null;
}

/**
 * PM reports `0` on a direction it does not measure (seen on both
 * QRO01 ↔ QRO02 links), so zero is treated as "no measurement".
 */
function measured(value: number | null | undefined): number | null {
    return value != null && value > 0 ? value : null;
}

function directionalDelay(
    own: LatencyDetail | null | undefined,
    opposite: LatencyDetail | null | undefined
): { delayUs: number | null; borrowed: boolean } {
    const ownUs = measured(own?.avgDelayUs);
    if (ownUs != null) return { delayUs: ownUs, borrowed: false };
    const oppositeUs = measured(opposite?.avgDelayUs);
    return { delayUs: oppositeUs, borrowed: oppositeUs != null };
}

function edgeCost(
    metric: LatencyPathMetric,
    igpCost: number,
    own: LatencyDetail | null | undefined,
    delayUs: number | null
): number | null {
    if (metric === 'igp') return igpCost;
    return measured(own?.minDelayUs) ?? delayUs;
}

/** Both traversal directions of one IS-IS link. */
function directedEdges(
    link: TopologyLink,
    metric: LatencyPathMetric,
    baseline: BaselineData | null
): DirectedEdge[] {
    const baselineLink = baseline?.links[link.id] ?? null;
    // The baseline borrows exactly like the live value does, so a
    // one-way-measured link still compares like with like.
    const baselineForward = measured(baselineLink?.forward.avgDelayUs);
    const baselineReverse = measured(baselineLink?.reverse.avgDelayUs);
    const sides = [
        {
            from: link.source,
            to: link.target,
            igpCost: link.forwardMetric ?? link.metric,
            own: link.forward,
            opposite: link.reverse,
            baselineUs: baselineForward ?? baselineReverse,
        },
        {
            from: link.target,
            to: link.source,
            igpCost: link.reverseMetric ?? link.metric,
            own: link.reverse,
            opposite: link.forward,
            baselineUs: baselineReverse ?? baselineForward,
        },
    ];

    const edges: DirectedEdge[] = [];
    for (const side of sides) {
        const { delayUs, borrowed } = directionalDelay(side.own, side.opposite);
        const cost = edgeCost(metric, side.igpCost, side.own, delayUs);
        if (cost == null) continue;
        edges.push({
            from: side.from,
            to: side.to,
            linkId: link.id,
            cost,
            delayUs,
            borrowed,
            baselineUs: side.baselineUs,
        });
    }
    return edges;
}

function buildAdjacency(
    data: TopologyData,
    metric: LatencyPathMetric,
    baseline: BaselineData | null
): Map<string, DirectedEdge[]> {
    const adjacency = new Map<string, DirectedEdge[]>();
    for (const link of data.links) {
        for (const edge of directedEdges(link, metric, baseline)) {
            const list = adjacency.get(edge.from) ?? [];
            list.push(edge);
            adjacency.set(edge.from, list);
        }
    }
    return adjacency;
}

/**
 * Dijkstra that keeps every equal-cost predecessor. Nodes in `noTransit`
 * (route reflectors) can be a destination but are never relaxed through.
 */
function shortestPathDag(
    adjacency: Map<string, DirectedEdge[]>,
    root: string,
    noTransit: Set<string>
): { dist: Map<string, number>; preds: Map<string, DirectedEdge[]> } {
    const dist = new Map<string, number>([[root, 0]]);
    const preds = new Map<string, DirectedEdge[]>();
    const settled = new Set<string>();
    const queue: { id: string; cost: number }[] = [{ id: root, cost: 0 }];

    while (queue.length > 0) {
        queue.sort((a, b) => a.cost - b.cost);
        const current = queue.shift();
        if (!current || settled.has(current.id)) continue;
        settled.add(current.id);
        if (current.id !== root && noTransit.has(current.id)) continue;

        for (const edge of adjacency.get(current.id) ?? []) {
            const candidate = current.cost + edge.cost;
            const known = dist.get(edge.to);
            if (known == null || candidate < known) {
                dist.set(edge.to, candidate);
                preds.set(edge.to, [edge]);
                queue.push({ id: edge.to, cost: candidate });
            } else if (candidate === known) {
                preds.get(edge.to)?.push(edge);
            }
        }
    }
    return { dist, preds };
}

/** Walk the predecessor DAG back from `target`, capped. */
function enumerateRoutes(
    preds: Map<string, DirectedEdge[]>,
    root: string,
    target: string
): { paths: DirectedEdge[][]; truncated: boolean } {
    const paths: DirectedEdge[][] = [];
    let truncated = false;

    const walk = (node: string, suffix: DirectedEdge[]): void => {
        if (truncated) return;
        if (node === root) {
            if (paths.length >= MAX_ROUTES_PER_PAIR) {
                truncated = true;
                return;
            }
            paths.push([...suffix].reverse());
            return;
        }
        for (const edge of preds.get(node) ?? []) {
            suffix.push(edge);
            walk(edge.from, suffix);
            suffix.pop();
        }
    };

    walk(target, []);
    return { paths, truncated };
}

function sumOrNull(values: (number | null)[]): number | null {
    let total = 0;
    for (const value of values) {
        if (value == null) return null;
        total += value;
    }
    return total;
}

function toRoute(path: DirectedEdge[]): LatencyRoute {
    return {
        hops: path.map((edge) => ({
            from: edge.from,
            to: edge.to,
            linkId: edge.linkId,
            delayUs: edge.delayUs,
            borrowed: edge.borrowed,
        })),
        delayUs: sumOrNull(path.map((edge) => edge.delayUs)),
        baselineUs: sumOrNull(path.map((edge) => edge.baselineUs)),
    };
}

function byDelayAscending(a: LatencyRoute, b: LatencyRoute): number {
    return (a.delayUs ?? Infinity) - (b.delayUs ?? Infinity);
}

function toCell(
    cost: number,
    paths: DirectedEdge[][],
    truncated: boolean
): LatencyCell {
    const routes = paths.map(toRoute).sort(byDelayAscending);
    const delays = routes.map((r) => r.delayUs);
    const known = delays.filter((d): d is number => d != null);
    const anyUnknown = known.length < delays.length;

    return {
        cost,
        routes,
        minUs: known.length > 0 ? Math.min(...known) : null,
        // An unmeasured branch could be the slow one, so no upper bound.
        maxUs: known.length > 0 && !anyUnknown ? Math.max(...known) : null,
        truncated,
        hasBorrowedHop: routes.some((r) => r.hops.some((h) => h.borrowed)),
    };
}

/** All routers present in the IS-IS links, sorted like the cost matrix. */
export function listMatrixRouters(data: TopologyData): string[] {
    const ids = new Set<string>();
    for (const link of data.links) {
        ids.add(link.source);
        ids.add(link.target);
    }
    return Array.from(ids).sort((a, b) => a.localeCompare(b));
}

export function computeLatencyMatrix(
    data: TopologyData,
    metric: LatencyPathMetric,
    baseline: BaselineData | null
): LatencyMatrixResult {
    const routers = listMatrixRouters(data);
    const adjacency = buildAdjacency(data, metric, baseline);
    const noTransit = new Set(
        data.nodes.filter((n) => n.type === 'route-reflector').map((n) => n.id)
    );

    const cells: LatencyMatrixResult['cells'] = {};
    for (const source of routers) {
        cells[source] = {};
        const { dist, preds } = shortestPathDag(adjacency, source, noTransit);
        for (const target of routers) {
            const cost = dist.get(target);
            if (target === source || cost == null) continue;
            const { paths, truncated } = enumerateRoutes(preds, source, target);
            cells[source][target] = toCell(cost, paths, truncated);
        }
    }
    return { routers, cells };
}

/**
 * The equal-cost route furthest above its own baseline, so one degraded
 * branch colours the cell even when the others are fine.
 */
export function worstRouteVsBaseline(cell: LatencyCell): LatencyRoute | null {
    let worst: LatencyRoute | null = null;
    let worstRatio = -Infinity;
    for (const route of cell.routes) {
        if (route.delayUs == null || route.baselineUs == null) continue;
        const ratio = route.delayUs / route.baselineUs;
        if (ratio > worstRatio) {
            worst = route;
            worstRatio = ratio;
        }
    }
    return worst;
}

/** ms with 2 decimals under 10 ms and 1 above, so 18 columns stay narrow. */
export function formatDelayMs(us: number): string {
    if (us < 10) return '<0.01';
    const ms = us / 1000;
    return ms < 10 ? ms.toFixed(2) : ms.toFixed(1);
}
